import Api from '../Api';

/** Как в `AddOrganization` / `DaysSection` */
export const organizationFormDays = [
  { name: 'Понедельник', value: 'monday' },
  { name: 'Вторник', value: 'tuesday' },
  { name: 'Среда', value: 'wednesday' },
  { name: 'Четверг', value: 'thursday' },
  { name: 'Пятница', value: 'friday' },
  { name: 'Суббота', value: 'saturday' },
  { name: 'Воскресенье', value: 'sunday' },
];

const DAY_KEYS = organizationFormDays.map((d) => d.value);

const defaultSchedule = organizationFormDays.reduce(
  (acc, day) => ({ ...acc, [day.value]: { start: '', end: '' } }),
  {},
);

export function stripJsonFromPastedText(raw) {
  if (typeof raw !== 'string') return '';
  let t = raw.trim();
  const fence = /^```(?:json)?\s*([\s\S]*?)```$/im;
  const m = t.match(fence);
  if (m) t = m[1].trim();
  return t;
}

/**
 * Загружает категории, страны, языки и все города по странам (как на форме выбора города).
 */
export async function fetchAllStaticDataForAutofill() {
  const [{ data: catData }, { data: countriesData }, { data: langData }] = await Promise.all([
    Api.getCategories(),
    Api.getCountries(),
    Api.getLanguages(),
  ]);

  const categories = catData?.categories || [];
  const countries = countriesData?.countries || [];
  const languages = langData?.languages || [];

  const citiesByCountryId = {};
  await Promise.all(
    countries.map(async (country) => {
      const { data } = await Api.getCities({ country_id: country._id });
      citiesByCountryId[country._id] = data?.cities || [];
    }),
  );

  return {
    categories,
    countries,
    languages,
    citiesByCountryId,
  };
}

function findCategoryById(categories, categoryId) {
  if (!categoryId) return null;
  return categories.find((c) => c._id === categoryId) || null;
}

function resolveSubCategoriesByIds(category, subCategoryIds) {
  if (!category?.sub_categories?.length || !Array.isArray(subCategoryIds) || !subCategoryIds.length) {
    return [];
  }
  const subs = category.sub_categories;
  return subs.filter((s) => subCategoryIds.includes(s._id));
}

export function findCityById(citiesByCountryId, cityId) {
  if (!cityId) return null;
  let found = null;
  Object.keys(citiesByCountryId).forEach((cid) => {
    if (found) return;
    const list = citiesByCountryId[cid];
    const f = list.find((c) => c._id === cityId);
    if (f) found = f;
  });
  return found;
}

function resolveLanguagesByIds(languages, languageIds) {
  if (!Array.isArray(languageIds) || !languageIds.length) return [];
  return languages.filter((l) => languageIds.includes(l._id));
}

function normalizeDayValue(val) {
  if (val === undefined || val === null) return null;

  if (typeof val === 'string') {
    const s = val.trim();
    if (s.includes('-') && /^\d{1,2}:\d{2}-\d{1,2}:\d{2}$/.test(s.replace(/\s/g, ''))) {
      const clean = s.replace(/\s/g, '');
      const [start, end] = clean.split('-');
      return { type: 'hours', start, end };
    }
    return { type: s, start: '', end: '' };
  }

  if (typeof val === 'object' && val.type === 'hours') {
    return {
      type: 'hours',
      start: val.start != null ? String(val.start) : '',
      end: val.end != null ? String(val.end) : '',
    };
  }

  if (typeof val === 'object' && val.type) {
    return { type: val.type, start: '', end: '' };
  }

  return null;
}

/** Только дни, явно присутствующие в объекте schedule */
function normalizeScheduleInputPartial(raw) {
  if (!raw || typeof raw !== 'object') return null;
  const out = {};
  DAY_KEYS.forEach((day) => {
    if (!Object.prototype.hasOwnProperty.call(raw, day)) return;
    const normalized = normalizeDayValue(raw[day]);
    if (normalized) out[day] = normalized;
  });
  return Object.keys(out).length ? out : null;
}

/** Только дни из work_time, как в API (частичное слияние с формой). */
export function scheduleFromWorkTimePartial(workTime) {
  if (!workTime || typeof workTime !== 'object') return null;
  const out = {};
  DAY_KEYS.forEach((day) => {
    if (!Object.prototype.hasOwnProperty.call(workTime, day)) return;
    const thisDayType = workTime[day];
    if (typeof thisDayType === 'string') {
      if (thisDayType.length === 11 && thisDayType.includes('-')) {
        const [start, end] = thisDayType.split('-');
        out[day] = { start, end, type: 'hours' };
      } else {
        out[day] = { start: '', end: '', type: thisDayType };
      }
    }
  });
  return Object.keys(out).length ? out : null;
}

const CONTACT_KEYS = ['phone_number', 'instagram', 'telegram', 'whatsapp', 'viber', 'facebook', 'website'];

/** Телефонные поля: в форме только цифры (без +, пробелов, скобок, дефисов). */
const PHONE_DIGIT_CONTACT_KEYS = ['phone_number', 'whatsapp', 'viber'];

export function normalizeContactDigits(value) {
  if (value == null || value === '') return '';
  return String(value).replace(/\D/g, '');
}

/**
 * Разбор JSON и сопоставление со справочниками.
 * Возвращает только поля, явно присутствующие в JSON (частичное заполнение).
 */
export function parseOrganizationAutofillJson(jsonString, staticData) {
  let data;
  try {
    data = JSON.parse(stripJsonFromPastedText(jsonString));
  } catch (e) {
    return { error: 'Не удалось разобрать JSON. Проверьте скобки и кавычки.' };
  }

  if (!data || typeof data !== 'object') {
    return { error: 'Корень JSON должен быть объектом.' };
  }

  const has = (k) => Object.prototype.hasOwnProperty.call(data, k);
  const categoryInJson = has('category_id');

  const {
    categories,
    languages,
    citiesByCountryId,
  } = staticData;

  const patches = {};

  if (has('owner_id')) patches.ownerId = String(data.owner_id).trim();
  if (has('name')) patches.name = String(data.name).trim();
  if (has('specialization')) patches.specialization = String(data.specialization).trim();
  if (has('address')) patches.address = String(data.address).trim();
  if (has('description')) patches.description = String(data.description).trim();

  if (has('schedule')) {
    const partial = normalizeScheduleInputPartial(data.schedule);
    if (partial) patches.schedulePartial = partial;
  } else if (has('work_time')) {
    const partial = scheduleFromWorkTimePartial(data.work_time);
    if (partial) patches.schedulePartial = partial;
  }

  if (has('contacts') && data.contacts && typeof data.contacts === 'object') {
    patches.contactsPartial = {};
    CONTACT_KEYS.forEach((k) => {
      if (Object.prototype.hasOwnProperty.call(data.contacts, k)) {
        const v = data.contacts[k];
        const raw = v != null ? String(v).trim() : '';
        patches.contactsPartial[k] = PHONE_DIGIT_CONTACT_KEYS.includes(k)
          ? normalizeContactDigits(raw)
          : raw;
      }
    });
  }

  let effectiveCategory = null;
  if (categoryInJson) {
    effectiveCategory = findCategoryById(categories, data.category_id);
    if (!effectiveCategory && data.category_id) {
      return { error: 'Категория не найдена по category_id.' };
    }
    patches.category = effectiveCategory;
  }

  if (has('sub_category_ids')) {
    if (!effectiveCategory) {
      return { error: 'Укажите category_id вместе с sub_category_ids.' };
    }
    patches.subCategories = resolveSubCategoriesByIds(effectiveCategory, data.sub_category_ids);
  }

  if (categoryInJson && effectiveCategory?.is_online) {
    patches.city = null;
    patches.additionalCities = [];
    if (has('address')) patches.address = '';
    if (has('coordinates')) patches.coordinates = null;
  } else {
    if (has('city_id')) {
      const city = findCityById(citiesByCountryId, data.city_id);
      if (!city) {
        return { error: 'Город не найден по city_id.' };
      }
      patches.city = city;
    }

    if (has('additional_city_ids')) {
      const ids = Array.isArray(data.additional_city_ids) ? data.additional_city_ids : [];
      patches.additionalCities = ids
        .map((id) => findCityById(citiesByCountryId, id))
        .filter(Boolean);
    }

    if (has('coordinates') && data.coordinates && (data.coordinates.lat != null || data.coordinates.lon != null)) {
      patches.coordinates = {
        lat: data.coordinates.lat != null ? String(data.coordinates.lat) : '',
        lon: data.coordinates.lon != null ? String(data.coordinates.lon) : '',
        is_special_point_on_map: Boolean(data.coordinates.is_special_point_on_map),
      };
    }
  }

  if (has('language_ids')) {
    patches.languages = resolveLanguagesByIds(languages, data.language_ids);
  }

  if (has('tags')) {
    patches.tags = Array.isArray(data.tags)
      ? data.tags.map((t) => String(t).trim()).filter(Boolean)
      : [];
  }

  if (has('template_photos_ids')) {
    patches.templatePhotoIds = Array.isArray(data.template_photos_ids)
      ? data.template_photos_ids.filter((id) => typeof id === 'string' && id.length === 24)
      : [];
  }

  return { patches };
}

export function buildAutofillReferenceFileContent(staticPayload) {
  const {
    categories,
    countries,
    languages,
    citiesByCountryId,
  } = staticPayload;

  const referenceData = {
    categories: categories.map((c) => ({
      _id: c._id,
      name: c.name,
      is_online: Boolean(c.is_online),
      sub_categories: (c.sub_categories || []).map((s) => ({ _id: s._id, name: s.name })),
    })),
    countries: countries.map((c) => ({ _id: c._id, name: c.name })),
    cities_by_country: countries.map((country) => ({
      country_id: country._id,
      cities: (citiesByCountryId[country._id] || []).map((city) => ({
        _id: city._id,
        name: city.name,
        country_id: city.country_id || country._id,
      })),
    })),
    languages: languages.map((l) => ({ _id: l._id, name: l.name })),
  };

  const schemaDescription = {
    description: 'Один JSON-объект для формы «Добавить организацию». Поля опускай, если в тексте нет данных. Справочники (категории, города, языки) сопоставляй по смыслу с REFERENCE_DATA, но в JSON для них пиши ТОЛЬКО значения полей _id из справочника — никогда не подставляй вместо id человекочитаемые названия из справочника.',
    fields: {
      owner_id: '24-символьный hex ID владельца в системе',
      name: 'Название организации',
      category_id: 'строка — ровно categories[i]._id',
      sub_category_ids: ['массив строк — только sub_categories[j]._id выбранной категории'],
      specialization: 'строка',
      city_id: 'строка — ровно cities[k]._id из cities_by_country',
      additional_city_ids: ['массив строк — только _id городов из справочника'],
      address: 'строка',
      coordinates: { lat: 'число или строка', lon: 'число или строка', is_special_point_on_map: false },
      schedule: 'объект дней monday..sunday; каждый день: { type: "hours", start, end } или { type: "day_off"|"temp_closed"|"whole_day" } или строка "09:00-18:00" / "day_off"',
      work_time: 'альтернатива schedule в формате API: monday: "09:00-18:00" или "day_off"',
      contacts: 'Объект: phone_number, whatsapp, viber — только цифры подряд (если в тексте +7 (999) 123-45-67, в JSON пиши 79991234567). instagram, telegram, facebook, website — как строки без изменения формата.',
      description: 'строка',
      language_ids: 'ОБЯЗАТЕЛЬНО: массив строк, каждая строка = languages[n]._id (поле _id из REFERENCE_DATA.languages). ЗАПРЕЩЕНО класть в language_ids названия языков (например «Русский») — только id из справочника.',
      tags: 'массив коротких строк — ключевые слова/темы по тексту; не из справочника. Если из текста можно вывести хотя бы одну тему — заполни минимум 1–3 тега, не оставляй пустым без причины.',
      template_photos_ids: ['опционально _id шаблонных фото'],
    },
    json_syntax: 'Формат ответа — как у JSON.stringify(объект) в JavaScript: один валидный JSON-текст, который целиком можно передать в JSON.parse() и получить объект. Те же правила сериализации: двойные кавычки, escape в строках (\\", \\\\, \\n и т.д.), без запятых в конце, без ключей без кавычек, без undefined/NaN/Function. Допустимо «красивое» форматирование с отступами, как JSON.stringify(obj, null, 2). Без markdown и без текста до/после JSON.',
    note: 'Для онлайн-категории (is_online: true) не включай city_id / additional_city_ids, если организация онлайн. Телефоны в contacts.phone_number, whatsapp, viber — строка из одних цифр, без плюса, скобок и разделителей.',
  };

  const prompt = [
    'Ты — помощник по заполнению карточки организации в админке Nomad.',
    '',
    'Порядок работы:',
    '— Сначала человек пришлёт тебе этот файл (справочник и инструкцию) одним сообщением. На это сообщение ты НЕ генерируешь JSON.',
    '— Если задача понятна, ответь ровно этой фразой и больше ничего: «Задача ясна, отправь текст для преобразования». Дальше жди следующих сообщений.',
    '— Когда в следующих сообщениях пришлют «сухой» текст об организации (описание, контакты, режим и т.д.), выполни шаги ниже и верни результат.',
    '',
    'Ниже в REFERENCE_DATA — категории с подкатегориями, города, языки (у каждой записи есть _id и name; name только для твоего сопоставления с текстом).',
    '',
    'КРИТИЧНО — языки (language_ids):',
    '— В JSON поле language_ids — это ВСЕГДА массив строк-идентификаторов: скопируй значение поля _id из нужных объектов в REFERENCE_DATA.languages.',
    '— ЗАПРЕЩЕНО писать в language_ids человекочитаемые названия («Русский», «English», «Հայերեն» и т.п.). Так программа не сможет разобрать ответ.',
    '— Алгоритм: по тексту понял язык → нашёл объект в languages с подходящим name → в массив language_ids добавил ТОЛЬКО его строку _id.',
    '— Пример правильно: "language_ids": ["507f1f77bcf86cd799439011", "507f191e810c19729de860ea"]',
    '— Пример НЕПРАВИЛЬНО: "language_ids": ["Русский", "English"]',
    '',
    'КРИТИЧНО — теги (tags):',
    '— Это массив обычных строк-слов/фраз из смысла текста (не из справочника id).',
    '— Выведи минимум 1–3 тега, если в тексте есть хоть какая-то предметная информация (услуги, тема, тип бизнеса). Пустой tags допустим только если текста совсем нет.',
    '',
    'КРИТИЧНО — телефоны (contacts.phone_number, contacts.whatsapp, contacts.viber):',
    '— В JSON значение должно быть «сухим»: только цифры 0–9 подряд, без +, пробелов, скобок, дефисов.',
    '— Если в исходном тексте номер в виде +7 (999) 123-45-67 или 8-999-123-45-67 — оставь в JSON только цифры в том порядке, как в номере (например 79991234567 или 89991234567 по смыслу страны).',
    '— Поля instagram, telegram, facebook, website не являются телефонами — не режь их до цифр.',
    '',
    'КРИТИЧНО — формат ответа = как после JSON.stringify в JavaScript:',
    '— Программа вставит твой текст в JSON.parse(...). Ответ должен быть таким же по правилам, как строка, которую возвращает JSON.stringify(объект) для обычного объекта с данными: валидный JSON по стандарту, который без правок парсится в JS.',
    '— Собери данные в один объект и сериализуй его так же, как JSON.stringify в JavaScript — без пояснений, без обёрток ```, только валидный JSON-текст.',
    '— Допустимо оформить с переносами и отступами (аналог JSON.stringify(obj, null, 2)) — это всё ещё один корректный аргумент для JSON.parse.',
    '— Запрещено: одинарные кавычки у ключей, запятая после последнего элемента, комментарии // или /* */, ключи без кавычек, значения undefined, неэкранированные кавычки внутри строк.',
    '— Внутри строк экранируй кавычки как \\" (как делает JSON.stringify).',
    '— Проверка: твой ответ целиком = единственный аргумент JSON.parse в браузере/Node без ошибки.',
    '',
    'Когда пришёл текст для преобразования, твоя задача:',
    '1) Извлеки из него всё, что относится к полям из schemaDescription.fields.',
    '2) Для категории, подкатегорий, города, доп. городов, языков подставляй только _id из REFERENCE_DATA (см. блоки про языки и теги выше).',
    '3) Не выдумывай id: каждый id должен буквально совпадать с каким-либо _id из соответствующего списка в REFERENCE_DATA.',
    '4) График (schedule): для каждого дня monday..sunday — часы { "type": "hours", "start", "end" } или тип day_off / temp_closed / whole_day.',
    '5) Верни одну строку ответа — ровно такой текст, который можно вставить в JSON.parse: как результат JSON.stringify для твоего объекта данных.',
    '',
    'REFERENCE_DATA:',
    JSON.stringify(referenceData, null, 2),
    '',
    'SCHEMA_HINT:',
    JSON.stringify(schemaDescription, null, 2),
  ].join('\n');

  return [
    '══════════════════════════════════════════════════════════════',
    'NOMAD — автозаполнение организации (справочник + инструкция для ИИ)',
    '══════════════════════════════════════════════════════════════',
    '',
    'Как пользоваться (оператору):',
    '1) Сохрани этот файл при необходимости.',
    '2) Первым сообщением отправь в чат с языковой моделью весь этот файл (или целиком блок ниже — REFERENCE_DATA и инструкция для модели).',
    '3) Дождись ответа модели. Если задача ясна, она должна написать: «Задача ясна, отправь текст для преобразования».',
    '4) Следующим сообщением (или несколькими) отправь сухой текст об организации.',
    '5) Скопируй ответ модели: это должен быть текст в формате JSON.stringify (его можно целиком передать в JSON.parse в консоли браузера). Вставь в поле «Автозаполнение» в админке, нажми «Преобразовать».',
    '6) Если ответ ИИ не принимается системой (неверная структура или синтаксис), открой https://jsonviewer.stack.hu/ — вставь текст, нажми «Format», исправь по подсказкам при необходимости и скопируй JSON обратно.',
    '',
    prompt,
  ].join('\n');
}
